import { useEffect, useRef, useCallback } from 'react';
import { useInfoMessages, useMachineStore } from '@/store/store';
import { TOAST_DISMISS_DELAY } from '@/types/machine.types';
import styles from '@/components/ToastNotifications/ToastNotifications.module.css';

/**
 * Toast notifications for non-critical messages (NML_TEXT, OPERATOR_TEXT).
 * Auto-dismiss after TOAST_DISMISS_DELAY (5s). Does NOT block the UI.
 * Displayed in the bottom-right corner.
 */
export function ToastNotifications(): JSX.Element | null {
  const messages = useInfoMessages();
  const clearInfoMessages = useMachineStore((s) => s.clearInfoMessages);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Auto-dismiss old info messages
  useEffect(() => {
    if (messages.length === 0) return;

    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
    }

    timerRef.current = setTimeout(() => {
      clearInfoMessages();
    }, TOAST_DISMISS_DELAY);

    return () => {
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current);
      }
    };
  }, [messages.length, clearInfoMessages]);

  const handleDismiss = useCallback(() => {
    clearInfoMessages();
  }, [clearInfoMessages]);

  if (messages.length === 0) return null;

  return (
    <div className={styles.toastContainer} role="log" aria-label="Notifications">
      {messages.slice(-5).map((msg) => (
        <div key={msg.id} className={styles.toastItem}>
          <span className={styles.toastItemIcon}>{msg.kind === 'OPERATOR_TEXT' ? 'ℹ️' : '📋'}</span>
          <span className={styles.toastItemMessage}>{msg.message}</span>
          <button
            className={styles.toastItemClose}
            onClick={handleDismiss}
            type="button"
            aria-label="Dismiss"
          >
            ✕
          </button>
        </div>
      ))}
    </div>
  );
}
